import qs from 'query-string'
import { z } from 'zod'

import { PAYMENT_SERVICE_BASE_URL } from '../config'

import axios from './../../function/axiosInstance'
import extract from './../../function/extract'

interface BillsPayload {
  queryParams: Partial<BillsQueryParams>
  current_page: number
}

interface BillIDPayload {
  billIDs: string[]
  status: string
  otp_id?: string | null
  otp?: string | null
}
interface BillOtpPayload {
  otp_id: string
  otp: string
}

export async function fetchPaymentBills({
  queryParams,
  current_page
}: BillsPayload): Promise<GetPaymentBills> {
  try {
    const queryParamsData = qs.stringify(
      {
        ...queryParams,
        current_page
      },
      { arrayFormat: 'comma', skipNull: true, skipEmptyString: true }
    )
    const response = await axios.get(`/creator-bills?${queryParamsData}`)
    return extract.data(response)
  } catch (error) {
    return {
      status: 'error',
      data: null,
      error: extract.error(error).error
    }
  }
}

export async function voidBill({
  billIDSet
}: {
  billIDSet: string[]
}): Promise<GetPaymentBills> {
  try {
    const response = await axios.put(`/creator-bills`, {
      status: 'void',
      creator_bills: billIDSet
    })
    return extract.data(response)
  } catch (error) {
    return {
      status: 'error',
      data: null,
      error: extract.error(error).error
    }
  }
}

export async function processReadyPayments({
  gteDate,
  lteDate,
  organization,
  otp,
  otpID
}: {
  gteDate: string
  lteDate: string
  organization: string
  otp: string
  otpID: string
}): Promise<GetPaymentBills> {
  try {
    const queryParamsData = qs.stringify(
      {
        'created_at.gte': gteDate,
        'created_at.lte': lteDate,
        organization
      },
      { skipNull: true, skipEmptyString: true }
    )
    const response = await axios.post(
      `/creator-bills/process-ready-payouts?${queryParamsData}`,
      {
        otp,
        otp_id: otpID
      }
    )
    return extract.data(response)
  } catch (error) {
    return {
      status: 'error',
      data: null,
      error: extract.error(error).error
    }
  }
}

export async function payoutOTPForManualAdjustment({
  creator_id,
  amount,
  description,
  pepper_organization,
  paymentReason,
  initiatePayout
}: {
  creator_id: string
  amount?: number | null
  description?: string
  pepper_organization?: string
  paymentReason?: string
  initiatePayout?: boolean
}): Promise<any> {
  try {
    const response = await axios.post(`/creator-bills/payout-otp`, {
      creator_id,
      amount,
      description,
      pepper_organization,
      payment_reason: paymentReason,
      initiate_payment: initiatePayout
    })
    return extract.data(response)
  } catch (error) {
    return {
      status: 'error',
      data: null,
      error: extract.error(error).error
    }
  }
}

export async function adhocBillSubmit(
  payload:
    | {
        type: 'ASSIGNMENT_DETAILS'
        creator_id: string
        initiatePayout: boolean
        otp: string
        otpID: string
        amount: number
        description: string
        pepper_organization: string
        paymentReason: string
      }
    | {
        type: 'ASSIGNMENT_IDS'
        creator_id: string
        initiatePayout: boolean
        otp: string
        otpID: string
        order_ids: string[]
      }
): Promise<GetPaymentBills> {
  const { creator_id, initiatePayout, otp, otpID } = payload
  let apiInput
  if (payload.type === 'ASSIGNMENT_IDS') {
    apiInput = {
      creator_id,
      initiate_payment: initiatePayout,
      otp,
      otp_id: otpID,
      order_ids: payload.order_ids
    }
  } else {
    apiInput = {
      creator_id,
      initiate_payment: initiatePayout,
      otp,
      otp_id: otpID,
      amount: payload.amount,
      description: payload.description,
      pepper_organization: payload.pepper_organization,
      payment_reason: payload.paymentReason
    }
  }

  try {
    const response = await axios.post(`/creator-bills`, apiInput)
    return extract.data(response)
  } catch (error) {
    return {
      status: 'error',
      data: null,
      error: extract.error(error).error
    }
  }
}

export async function billUpdate({
  otp,
  otp_id,
  billIDs,
  status
}: BillIDPayload): Promise<ApiResponse> {
  try {
    const response = await axios.put('/creator-bills', {
      creator_bills: billIDs,
      status,
      otp,
      otp_id
    })
    return extract.data(response)
  } catch (error) {
    return {
      status: 'error',
      data: null,
      error: extract.error(error).error
    }
  }
}
export async function fetchPaymentBillsSummary(
  currency_code: Partial<BillsQueryParams>
): Promise<GetPaymentBillSummary> {
  try {
    const currencyParam = qs.stringify(currency_code)

    const response = await axios.get(`/creator-bills/summary?${currencyParam}`)
    return extract.data(response)
  } catch (error) {
    return {
      status: 'error',
      data: null,
      error: extract.error(error).error
    }
  }
}

export async function fetchPaymentBillsFiltersSummary(
  queryParams: Partial<BillsQueryParams>
): Promise<GetPaymentBillSummary> {
  try {
    const queryParamsData = qs.stringify(
      {
        ...queryParams
      },
      { arrayFormat: 'comma', skipNull: true, skipEmptyString: true }
    )
    const response = await axios.get(
      `/creator-bills/summary?${queryParamsData}`
    )
    return extract.data(response)
  } catch (error) {
    return {
      status: 'error',
      data: null,
      error: extract.error(error).error
    }
  }
}

export async function verifyBillOtp({
  otp,
  otp_id
}: BillOtpPayload): Promise<ApiResponse> {
  try {
    const response = await axios.post('/verify-otp', { otp, otp_id })
    return extract.data(response)
  } catch (error) {
    return {
      status: 'error',
      data: null,
      error: extract.error(error).error
    }
  }
}
export async function sendBillPaymentOtp(): Promise<ApiResponse> {
  try {
    const response = await axios.post('/creator-bills/payout-otp', {})
    return extract.data(response)
  } catch (error) {
    return {
      status: 'error',
      data: null,
      error: extract.error(error).error
    }
  }
}

//BILL RECEIPTS DATA FOR Creator Earnings
interface billReceIptsUpdate {
  billId?: string
}
export async function billReceiptsUpdate({
  billId
}: billReceIptsUpdate): Promise<ApiResponse> {
  try {
    const url = `/creator-bills/receipts/${billId}`
    const response = await axios.get(url)
    return extract.data(response)
  } catch (error) {
    return extract.error(error)
  }
}

export async function fetchPaymentBillsReceipts({
  queryParams,
  current_page
}: BillsPayload): Promise<GetPaymentBills> {
  try {
    const queryParamsData = qs.stringify(
      {
        ...queryParams,
        page: current_page
      },
      { skipNull: true, skipEmptyString: true }
    )
    const response = await axios.get(
      `${PAYMENT_SERVICE_BASE_URL}/bills?${queryParamsData}`
    )
    return extract.data(response)
  } catch (error) {
    return {
      status: 'error',
      data: null,
      error: extract.error(error).error
    }
  }
}

export async function getStripeURL({
  payment_service_account_id
}: {
  payment_service_account_id: string
}): Promise<ApiResponse> {
  try {
    const response = await axios.get(
      `${PAYMENT_SERVICE_BASE_URL}/payment-onboarding/stripe-onboarding-link?account=${payment_service_account_id}&organization=pepper_content_inc&refresh_url=https://creators.peppercontent.io/profile/banking?status=refresh&return_url=https://creators.peppercontent.io/profile/banking?status=success`
    )
    return extract.data(response)
  } catch (error) {
    return {
      status: 'error',
      data: null,
      error: extract.error(error).error
    }
  }
}

export async function getPaymentMethods({
  payment_service_account_id
}: {
  payment_service_account_id: string
}): Promise<ApiResponse> {
  try {
    const response = await axios.get(
      `${PAYMENT_SERVICE_BASE_URL}/accounts/${payment_service_account_id}/payment-methods`
    )
    return extract.data(response)
  } catch (error) {
    return {
      status: 'error',
      data: null,
      error: extract.error(error).error
    }
  }
}

export async function getUserPanGst({
  payment_service_account_id
}: {
  payment_service_account_id: string
}): Promise<ApiResponseV2<any>> {
  try {
    const response = await axios.get(
      `${PAYMENT_SERVICE_BASE_URL}/accounts/${payment_service_account_id}?expand=currency`
    )
    return extract.data(response)
  } catch (error) {
    return {
      status: 'error',
      data: null,
      error: extract.error(error).error
    }
  }
}

export async function getStripeStatus({
  payment_service_account_id
}: {
  payment_service_account_id: string
}): Promise<ApiResponse> {
  try {
    const response = await axios.get(
      `${PAYMENT_SERVICE_BASE_URL}/payment-onboarding/stripe-onboarding-status?account=${payment_service_account_id}&&organization=pepper_content_inc`
    )
    return extract.data(response)
  } catch (error) {
    return {
      status: 'error',
      data: null,
      error: extract.error(error).error
    }
  }
}

const Payment = z.object({
  user_id: z.number(),
  total_days: z.number(),
  worked_days: z.number(),
  payment_entity: z.string(),
  error: z.string().optional()
})

const PaymentSchema = z.object({
  payments: z.array(Payment)
})

type BillInput = z.infer<typeof PaymentSchema>

const BillResponseSchema = z.object({
  status: z.literal('error').or(z.literal('success')),
  error_entries: z.optional(z.array(Payment)),
  message: z.optional(z.string())
})
type BillResonse = z.infer<typeof BillResponseSchema>

export const createEditorBills = async (
  data: BillInput
): Promise<
  ApiResponseV2<BillResonse, BillResonse['error_entries'] | string>
> => {
  try {
    PaymentSchema.parse(data)
    const request = await axios.post<BillResonse>(
      '/creator-payments/pay-per-month',
      data
    )

    BillResponseSchema.parse(request.data)

    return extract.data<BillResonse>(request)
  } catch (error) {
    if (error instanceof z.ZodError) {
      return extract.error(error)
    }

    return {
      status: 'error',
      error: error?.response?.data?.error_entries ?? [],
      data: null
    }
  }
}

export const updatePaymentsStatus = async ({
  assignments,
  status
}: {
  assignments: string[]
  status: 'on_hold' | 'ready'
}) => {
  try {
    z.array(z.string()).parse(assignments)

    const request = await axios.put('/assignments/payment/status-update', {
      assignment_ids: assignments,
      status
    })
    return extract.data(request.data)
  } catch (error) {
    return extract.error(error)
  }
}
